import {
  AddressDetails,
  DatalistOption,
  InsuranceJourneyMinMaxDobType,
  KeyValuePair,
  ProductType,
  QuoteHeader,
  QuoteProduct,
  QuoteProductFilter,
  QuotesDto,
  SelectOpt,
  User,
  VehicleState
} from './CommonTypes';
import { CommonValidation } from './CommonValidation/commonValidation';
import { PaymentMethod } from './Constants';
import { MarketingPreferenceOptions } from './MarketingPreferences';
import { getSelectedVariant } from './ProductMethods';

export const getFormattedDate = (dateText: string | undefined): string => {
  if (CommonValidation.isEmpty(dateText)) return '';

  const jsonDate = CommonValidation.getJsonDate(dateText);

  if (!jsonDate) {
    return '';
  }

  const formattedDate = new Date(jsonDate).toLocaleDateString('en-GB');

  return formattedDate;
};

export const getDateString = (dateText: string | undefined): string => {
  if (CommonValidation.isEmpty(dateText)) return '';

  const jsonDate = CommonValidation.getJsonDate(dateText);

  if (!jsonDate) {
    return '';
  }

  const stringDate = new Date(jsonDate).toDateString();

  return stringDate;
};

export const getJsonDateString = (dateText: string | undefined): string => {
  if (CommonValidation.isEmpty(dateText)) return '';

  const stringDate = JSON.stringify({
    value: dateText,
    utc: '0'
  });

  return stringDate;
};

export const getQuotesData = (
  products: ProductType[],
  vehicle: VehicleState,
  user: User,
  quoteId: number,
  emailConfirm: string,
  title: string,
  surname: string,
  marketingOptions: MarketingPreferenceOptions,
  quoteProducts: QuoteProduct[],
  quoteType: number,
  channels: string,
  dealerCode?: string
): QuotesDto => {
  const newQuoteProducts: QuoteProduct[] = [];

  products.forEach((product) => {
    if (product.isSelected) {
      const selectedQuoteProduct = getQuoteProduct(
        product,
        dealerCode === undefined ? user.userId : emailConfirm
      );

      newQuoteProducts.push(selectedQuoteProduct);
    } else {
      const existingQuoteProduct = quoteProducts.find(
        (qp) => qp.productTypeId === product.productTypeId
      );
      if (existingQuoteProduct) {
        newQuoteProducts.push(existingQuoteProduct);
      }
    }
  });

  const registrationDateSplit =
    vehicle.vehicleDetails.registrationDate.split('/');
  const registrationDate = new Date(
    parseInt(registrationDateSplit[2]),
    parseInt(registrationDateSplit[1]) - 1,
    parseInt(registrationDateSplit[0])
  ).setHours(0);

  const purchaseDateSplit = getFormattedDate(vehicle.vehicleDeliveryDate).split(
    '/'
  );
  const purchaseDate = new Date(
    parseInt(purchaseDateSplit[2]),
    parseInt(purchaseDateSplit[1]) - 1,
    parseInt(purchaseDateSplit[0])
  ).setHours(0);

  const gapIntroductionDateSplit = getFormattedDate(
    vehicle.vehicleGapIntroductionDate
  ).split('/');
  const gapIntroductionDate = new Date(
    parseInt(gapIntroductionDateSplit[2]),
    parseInt(gapIntroductionDateSplit[1]) - 1,
    parseInt(gapIntroductionDateSplit[0])
  ).setHours(0);

  const productFilter: QuoteProductFilter = {
    dealerNumber: dealerCode === undefined ? user.dealerCode : dealerCode,
    dateFirstRegistration:
      vehicle.vehicleDetails.registrationDate !== undefined
        ? new Date(registrationDate).toISOString()
        : undefined,
    purchaseDate: vehicle.vehicleDeliveryDate
      ? new Date(purchaseDate).toISOString()
      : undefined,
    vehicleValue: vehicle.vehiclePurchasePrice,
    distance: vehicle.currentMileage || 0,
    modelTrimId: vehicle.vehicleDetails.modelTrimId,
    fuelType: vehicle.vehicleDetails.fuelTypeCode,
    engineSize: parseInt(vehicle.vehicleDetails.engineSize),
    enginePowerBHP: vehicle.vehicleDetails.enginePowerBHP ?? 0,
    enginePowerKWH: vehicle.vehicleDetails.enginePowerKWH ?? 0,
    driveType: vehicle.vehicleDetails.driveTypeId?.toString() ?? '',
    channels: channels,
    versionNumber: 0,
    promoCode: '',
    gapIntroductionDate: vehicle.vehicleGapIntroductionDate
      ? new Date(gapIntroductionDate).toISOString()
      : undefined
  };

  const quoteHeader: QuoteHeader = {
    id: quoteId,
    password: 'hashed value',
    customerEmail: emailConfirm,
    customerTitle: title,
    customerSurname: surname,
    vrn: vehicle.vehicleDetails.vrn,
    vin: vehicle.vehicleDetails.vin ?? '',
    vehicleMake: vehicle.vehicleDetails.make,
    vehicleModel: vehicle.vehicleDetails.model,
    doNotContact: marketingOptions.doNotContact,
    createdBy: dealerCode === undefined ? user.userId : emailConfirm,
    createdOn: new Date(),
    deliveryDateEstimated: vehicle.deliveryDateEstimated,
    quoteType: quoteType,
    dealerReference: vehicle.dealerReference
  };

  return {
    quoteHeader: quoteHeader,
    productFilter: productFilter,
    quoteProducts: newQuoteProducts
  };
};

const getQuoteProduct = (
  product: ProductType,
  userEmail: string
): QuoteProduct => {
  const selectedProductVariant = getSelectedVariant(product.productVariants);
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 60);
  return {
    productTypeId: product.productTypeId,
    variantId: selectedProductVariant?.variantId ?? '',
    variantName: selectedProductVariant?.customerFriendlyName ?? '',
    productTypeName: product.productName,
    quotedPrice: selectedProductVariant?.price ?? 0,
    createdBy: userEmail,
    versionNumber: 0,
    createdOn: new Date().toISOString(),
    expiryDate: expiryDate.toISOString(),
    productSubTypeId: product.productSubTypeId
  };
};

export const setCountryCode = (
  absolutePath: string,
  countryCode: string
): string => {
  const replaceValue = countryCode ? `/${countryCode.toLowerCase()}` : '';

  return absolutePath.toLowerCase().replace('/:countrycode?', replaceValue);
};

export const getSelectedNoOfInstallments = (product: ProductType) => {
  if (product?.productVariants) {
    const selectedVariant = product.productVariants.find((v) => v.isSelected);

    if (selectedVariant) {
      if (selectedVariant.paymentMethod === PaymentMethod.Subscription) {
        return Math.max(
          ...product.productVariants.map((v) => v.noOfInstallments),
          0
        );
      }
      return selectedVariant.noOfInstallments ?? 0;
    } else {
      return Math.max(
        ...product.productVariants.map((v) => v.noOfInstallments),
        0
      );
    }
  } else {
    return 0;
  }
};

export const renderRadioOptions = (
  optionsToDisplay: string[],
  controlGroupLabel: string,
  value: string,
  onChange: (value: string) => void
): JSX.Element[] => {
  return optionsToDisplay.map((option, index) => {
    const controlId = `${controlGroupLabel}-${option}`;

    return (
      <div key={controlId} className='form-check form-check-inline'>
        <input
          data-testid={`test-${option.replace(' ', '-')}-${controlGroupLabel}-${index}`}
          className='form-check-input'
          type='radio'
          name={controlId}
          id={controlId}
          value={value}
          onChange={() => onChange(option)}
          checked={value === option}
        />
        <label className='form-check-label' htmlFor={controlId}>
          {option}
        </label>
      </div>
    );
  });
};

export const renderRadioSelectOptions = (
  optionsToDisplay: SelectOpt[],
  controlGroupLabel: string,
  value: string,
  onChange: (value: string) => void
): JSX.Element[] => {
  return optionsToDisplay.map((option, index) => {
    const controlId = `${controlGroupLabel}-${option.value}`;

    return (
      <div key={controlId} className='form-check form-check-inline'>
        <input
          data-testid={`test-${option.value}-${controlGroupLabel}-${index}`}
          className='form-check-input'
          type='radio'
          name={controlId}
          id={controlId}
          value={value}
          onChange={() => onChange(option.value)}
          checked={value === option.value}
        />
        <label className='form-check-label' htmlFor={controlId}>
          {option.text}
        </label>
      </div>
    );
  });
};

export const populateDatalist = (
  optionList: KeyValuePair[]
): DatalistOption[] => {
  return optionList.map((s) => {
    return {
      text: s.value,
      value: s.key
    } as DatalistOption;
  });
};

export const populateSelectOptions = (
  optionsList: KeyValuePair[]
): SelectOpt[] => {
  return optionsList.map((s) => {
    return {
      id: s.key,
      text: s.value,
      value: s.key,
      disabled: false
    } as SelectOpt;
  });
};

export const getDynamicHelpText = (
  HelpText: string,
  valueToReplace: string,
  replaceWith: string
): string => {
  return HelpText.replaceAll(valueToReplace, replaceWith);
};

export const getInsuranceJourneyMinMaxDob =
  (): InsuranceJourneyMinMaxDobType => {
    const today = new Date();

    return {
      maxDob: new Date(today.getFullYear() - 17, today.getMonth()),
      minDob: new Date(today.getFullYear() - 120, today.getMonth())
    };
  };

export const getMonthNames = (): SelectOpt[] => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const monthNameOptions: SelectOpt[] = [];
  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    const monthName = monthNames[monthIndex];
    const item: SelectOpt = {
      id: 'M' + monthIndex,
      value: monthName,
      text: monthName,
      disabled: false
    };
    monthNameOptions.push(item);
  }
  return monthNameOptions;
};

export const someAddressIsSet = (address: AddressDetails): boolean => {
  if (!address) return false;

  if (
    CommonValidation.isNotEmpty(address.addressLine1) ||
    CommonValidation.isNotEmpty(address?.addressLine2) ||
    CommonValidation.isNotEmpty(address?.townOrCity) ||
    CommonValidation.isNotEmpty(address?.region) ||
    CommonValidation.isNotEmpty(address?.postCode) ||
    CommonValidation.isNotEmpty(address?.country)
  ) {
    return true;
  }

  return false;
};

export const InsuranceJourney = {
  isAgeValid: (dateOfBirth: Date | undefined): boolean => {
    const today = new Date();
    if (dateOfBirth === undefined) {
      return false;
    }

    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())
    ) {
      age--;
    }

    const result = age >= 17 && age <= 90;
    return result;
  }
};

export const capitalizeFirstLetter = (val: string): string => {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
};

export const logError = (error: string) => {
  console.log(`Get cover details error: ${error}`);
};

export const getTextValueForDropdown = (
  options: SelectOpt[],
  value: string
) => {
  const option = options.find((o) => o.value === value);

  return option ? option.text : '';
};

export const isValidName = (input: string) => {
  //eslint-disable-next-line
  const regex = /^[a-zA-Z\s'-]*$/;
  return regex.test(input);
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

export const getIsoDate = (value: string | Date | null | undefined): string => {
  if (!value) {
    return '';
  }

  return value instanceof Date
    ? value.toISOString()
    : new Date(value).toISOString();
};

export const getPaymentMethodDisplayName = (
  paymentMethod: string,
  paymentMethodDisplayNames: KeyValuePair[]
) => {
  const displayName = paymentMethodDisplayNames?.find(
    (pm) => pm.key === paymentMethod
  )?.value;

  return displayName ?? paymentMethod;
};
